import React, {
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useState,
} from "react";
import PropTypes from "prop-types";

import { usePlaces } from "./usePlaces";
import {
  DefaultProps,
  Grid,
  Input,
  InputSharedProps,
  InputWrapperBaseProps,
  MantineSize,
  Paper,
  Stack,
  TextInput,
  TextInputStylesNames,
  useInputProps,
  ActionIcon,
  Switch,
  Group,
  SwitchProps,
} from "@mantine/core";

import { IAddress, IAddressModel } from "./types";
import { AddressRightSection } from "./AddressRightSection";
import {
  IconMap,
  IconMapPin,
  IconMapPinOff,
  IconChevronDown,
  IconChevronUp,
} from "@tabler/icons-react";
import { getAddressObject } from "./utils";
import { UseFormReturnType } from "@mantine/form";

export interface PlacesInputProps
  extends DefaultProps<TextInputStylesNames>,
    InputSharedProps,
    InputWrapperBaseProps,
    Omit<
      React.ComponentPropsWithoutRef<"input">,
      "size" | "icon" | "placeholder" | "form" | "defaultValue"
    > {
  /** Props passed to root element (InputWrapper component) */
  wrapperProps?: Record<string, any>;

  /** Input size */
  size?: MantineSize;

  icon?: React.ReactNode;

  noIcon?: boolean;

  placeholder?: string;

  /** Array list of country iso2 codes to restrict the addresses */
  countryRestrictions?: string[];

  /** Allow to clear item */
  clearable?: boolean;

  spacing?: MantineSize;

  addressComponents?: boolean;
  showAddressComponents?: boolean;
  manual?: boolean;
  manualSwitchProps?: SwitchProps;
  addressComponentsLabel?: React.ReactNode;

  apiKey?: string;
  libraries?: string[];
  inputAutocompleteValue?: string;
  options?: any;
  googleMapsScriptBaseUrl?: string;
  language?: string;
  type: "Address" | "City" | "Region" | "Country" | "Any";

  onPlaceSelected?: (place?: google.maps.places.PlaceResult) => void;
  onAddressChange?: (address?: IAddress) => void;

  // form?: UseFormReturnType<Partial<IAddressModel>>;
  // /** parent name e.g. "shippingAddress" */
  // propertyName?: string;

  countryPicker?: ReactNode;
  stateProvincePicker?: ReactNode;
  cityPicker?: ReactNode;

  defaultValue?: IAddressModel;
  __staticSelector?: string;
}

const defaultProps: Partial<PlacesInputProps> = {
  size: "sm",
  __staticSelector: "AddressInput",
};

export const PlacesInput = forwardRef<HTMLInputElement, PlacesInputProps>(
  (props, ref) => {
    const {
      inputProps,
      icon,
      noIcon,
      placeholder,
      wrapperProps,
      countryRestrictions = [],
      onAddressChange,
      spacing,
      clearable = true,
      readOnly,
      onPlaceSelected,
      apiKey,
      libraries,
      inputAutocompleteValue,
      options,
      googleMapsScriptBaseUrl,
      language,
      type,
      addressComponents,
      showAddressComponents,
      manual,
      manualSwitchProps,
      addressComponentsLabel,
      // form,
      // propertyName,

      countryPicker,
      stateProvincePicker,
      cityPicker,

      defaultValue,
      ...others
    } = useInputProps("PlacesInput", defaultProps, props);

    const { withAsterisk } = wrapperProps;

    const [address, setAddress] = useState<Partial<IAddress>>();
    const [override, setOverride] = useState<boolean>(true);
    const [enableGoogle, setEnableGoogle] = useState<boolean>(true);
    const [manualEntry, setManualEntry] = useState<boolean>(manual ?? false);
    const [showAddressComponentsInternal, setShowAddressComponents] =
      useState<boolean>(showAddressComponents ?? false);
    const [placeInternal, setPlaceInternal] =
      useState<google.maps.places.PlaceResult>();
    const [searchValue, setSearchValue] = useState<string>("");

    //const prefix = propertyName ? propertyName + "." : "";

    const onPlceSelectedInternal = useCallback(
      (place?: google.maps.places.PlaceResult) => {
        setPlaceInternal(place);
        if (place && type === "Address") {
          const addr = getAddressObject(place);
          setAddress(addr);

          if (plRef.current && addr.FormattedAddress) {
            plRef.current.value = addr.FormattedAddress;
          }
          // if (form) {
          //   form.setFieldValue(
          //     `${prefix}FormattedAddress`,
          //     addr?.FormattedAddress
          //   );
          //   form.setFieldValue(`${prefix}Unit`, addr?.Unit?.LongName);
          //   form.setFieldValue(
          //     `${prefix}StreetNumber`,
          //     addr?.StreetNumber?.LongName
          //   );
          //   form.setFieldValue(
          //     `${prefix}StreetAddress`,
          //     addr?.StreetAddress?.LongName
          //   );
          //   form.setFieldValue(
          //     `${prefix}ZipPostalCode`,
          //     addr?.PostalCode?.LongName
          //   );
          //   form.setFieldValue(`${prefix}City`, addr?.City);
          //   form.setFieldValue(`${prefix}StateProvince`, addr?.Region);
          //   form.setFieldValue(`${prefix}Country`, addr?.Country);
          //   form.setFieldValue(`${prefix}GooglePlaceId`, addr?.GooglePlaceId);
          //   form.setFieldValue(`${prefix}Lat`, addr?.Lat);
          //   form.setFieldValue(`${prefix}Lng`, addr?.Lng);
          // }
          onAddressChange?.(addr as IAddress);
        } else {
          setAddress(undefined);
          onAddressChange?.(undefined);
        }
      },
      [type, onAddressChange]
    );

    const { ref: plRef, autocompleteRef } = usePlaces({
      ref: ref,
      googleMapsScriptBaseUrl,
      onPlaceSelected: onPlceSelectedInternal,
      apiKey,
      libraries,
      inputAutocompleteValue,
      options: {
        componentRestrictions: { country: [...countryRestrictions] },
      },
      language,
      type,
    });

    const handleClear = () => {
      if (!readOnly) {
        //   handleChange(null);
        //   if (!controlled) {
        //     handleSearchChange('');
        //   }
        if (plRef.current) {
          plRef.current.value = "";
          setSearchValue("");
          setAddress(undefined);
          onAddressChange?.(undefined);
        }
      }
    };

    useEffect(() => {
      const cur = plRef.current;
      if (cur) {
        cur.addEventListener("change", () => {
          setSearchValue(cur.value ?? "");
        });
      }
      return () => {
        if (cur) {
          cur.removeEventListener("change", () => {});
        }
      };
    });

    useEffect(() => {
      onAddressChange?.(address as any);
    }, [address]);

    useEffect(() => {}, [defaultValue]);

    return (
      <Stack spacing={spacing}>
        <Input.Wrapper
          {...wrapperProps}
          sx={{ display: manualEntry ? "none" : "block" }}
        >
          <Input.Description>
            <Group>
              <Switch
                label="Manual"
                mb={5}
                size="xs"
                checked={manualEntry}
                onChange={(event) =>
                  setManualEntry(event.currentTarget.checked)
                }
                {...manualSwitchProps}
              />
              {/* {addressComponents && (
                <Switch
                  label="Manual address entry"
                  mb={5}
                  size="xs"
                  checked={manualEntry}
                  onChange={(event) =>
                    setManualEntry(event.currentTarget.checked)
                  }
                />
              )} */}
              <Switch
                label="Show address components"
                mb={5}
                size="xs"
                checked={showAddressComponentsInternal}
                onChange={(event) =>
                  setShowAddressComponents(event.currentTarget.checked)
                }
              />
            </Group>
          </Input.Description>

          <Input<"input">
            autoComplete="off"
            type="search"
            {...inputProps}
            {...others}
            defaultValue={defaultValue?.FormattedAddress}
            ref={plRef}
            icon={<IconMapPin size={16} />}
            placeholder={placeholder ?? "Start typing the address or postcode"}
            rightSectionWidth={100}
            rightSection={
              <Group position="right" w="100%" spacing={0} mr="xs">
                <AddressRightSection
                  onClear={handleClear}
                  shouldClear={clearable && searchValue !== ""}
                />
                <ActionIcon
                  onClick={() => {
                    setShowAddressComponents(!showAddressComponentsInternal);
                  }}
                >
                  {showAddressComponentsInternal ? (
                    <IconChevronUp size={16} />
                  ) : (
                    <IconChevronDown size={16} />
                  )}
                </ActionIcon>
              </Group>
            }
          />
        </Input.Wrapper>

        {addressComponents && (showAddressComponentsInternal || manualEntry) && (
          <>
            <Stack spacing="xs">
              {manualEntry && (
                <Input.Wrapper {...wrapperProps}>
                  <Input.Description>
                    <Group>
                      <Switch
                        label="Manual"
                        mb={5}
                        size="xs"
                        checked={manualEntry}
                        onChange={(event) =>
                          setManualEntry(event.currentTarget.checked)
                        }
                        {...manualSwitchProps}
                      />
                    </Group>
                  </Input.Description>
                </Input.Wrapper>
              )}
              {!manualEntry && (
                <Input.Label required={withAsterisk}>
                  {addressComponentsLabel ?? "Address details:"}
                </Input.Label>
              )}
              <Paper withBorder p="sm">
                <Stack spacing={spacing}>
                  <Grid>
                    <Grid.Col md={12}>
                      {manualEntry && countryPicker ? (
                        countryPicker
                      ) : (
                        <TextInput
                          label="Country:"
                          value={address?.Country?.LongName ?? ""}
                          defaultValue={defaultValue?.Country?.LongName}
                          onChange={(e) =>
                            manualEntry
                              ? setAddress({
                                  ...address,
                                  Country: {
                                    LongName: e.target.value,
                                    ShortName: e.target.value,
                                  },
                                })
                              : undefined
                          }
                          // {...(form
                          //   ? manualEntry
                          //     ? form.getInputProps(`${prefix}Country`)
                          //     : form.getInputProps(`${prefix}Country.LongName`)
                          //   : null)}
                        />
                      )}
                    </Grid.Col>
                    <Grid.Col md={12}>
                      {manualEntry && stateProvincePicker ? (
                        stateProvincePicker
                      ) : (
                        <TextInput
                          label="State/Province:"
                          value={address?.StateProvince?.LongName ?? ""}
                          defaultValue={defaultValue?.StateProvince?.LongName}
                          onChange={(e) =>
                            manualEntry
                              ? setAddress({
                                  ...address,
                                  StateProvince: {
                                    LongName: e.target.value,
                                    ShortName: e.target.value,
                                  },
                                })
                              : undefined
                          }
                          // {...(form
                          //   ? manualEntry
                          //     ? form.getInputProps(`${prefix}StateProvince`)
                          //     : form.getInputProps(
                          //         `${prefix}StateProvince.LongName`
                          //       )
                          //   : null)}
                        />
                      )}
                    </Grid.Col>
                    <Grid.Col md={12}>
                      {manualEntry && cityPicker ? (
                        cityPicker
                      ) : (
                        <TextInput
                          label="City/Suburb:"
                          value={address?.City?.LongName ?? ""}
                          defaultValue={defaultValue?.City?.LongName}
                          onChange={(e) =>
                            manualEntry
                              ? setAddress({
                                  ...address,
                                  City: {
                                    LongName: e.target.value,
                                    ShortName: e.target.value,
                                  },
                                })
                              : undefined
                          }
                          // {...(form
                          //   ? manualEntry
                          //     ? form.getInputProps(`${prefix}City`)
                          //     : form.getInputProps(`${prefix}City.LongName`)
                          //   : null)}
                        />
                      )}
                    </Grid.Col>
                  </Grid>
                  <Grid>
                    <Grid.Col md={2}>
                      <TextInput
                        label="Unit:"
                        value={address?.Unit?.LongName ?? ""}
                        defaultValue={defaultValue?.Unit}
                        onChange={(e) =>
                          manualEntry
                            ? setAddress({
                                ...address,
                                Unit: {
                                  LongName: e.target.value,
                                  ShortName: e.target.value,
                                },
                              })
                            : undefined
                        }
                        // {...(form ? form.getInputProps(`${prefix}Unit`) : null)}
                      />
                    </Grid.Col>
                    <Grid.Col md={4}>
                      <TextInput
                        label="Number:"
                        value={address?.StreetNumber?.LongName ?? ""}
                        defaultValue={defaultValue?.StreetNumber}
                        onChange={(e) =>
                          manualEntry
                            ? setAddress({
                                ...address,
                                StreetNumber: {
                                  LongName: e.target.value,
                                  ShortName: e.target.value,
                                },
                              })
                            : undefined
                        }
                        // {...(form
                        //   ? form.getInputProps(`${prefix}StreetNumber`)
                        //   : null)}
                      />
                    </Grid.Col>
                  </Grid>
                  <Grid>
                    <Grid.Col md={12}>
                      <TextInput
                        label="Street address:"
                        value={address?.StreetAddress?.LongName ?? ""}
                        defaultValue={defaultValue?.StreetAddress}
                        onChange={(e) =>
                          manualEntry
                            ? setAddress({
                                ...address,
                                StreetAddress: {
                                  LongName: e.target.value,
                                  ShortName: e.target.value,
                                },
                              })
                            : undefined
                        }
                        // {...(form
                        //   ? form.getInputProps(`${prefix}StreetAddress`)
                        //   : null)}
                      />
                    </Grid.Col>
                    <Grid.Col md={12}>
                      <TextInput
                        label="Postal code:"
                        value={address?.PostalCode?.LongName ?? ""}
                        defaultValue={defaultValue?.ZipPostalCode}
                        onChange={(e) =>
                          manualEntry
                            ? setAddress({
                                ...address,
                                PostalCode: {
                                  LongName: e.target.value,
                                  ShortName: e.target.value,
                                },
                              })
                            : undefined
                        }
                        // {...(form
                        //   ? form.getInputProps(`${prefix}ZipPostalCode`)
                        //   : null)}
                      />
                    </Grid.Col>
                  </Grid>
                </Stack>
              </Paper>
            </Stack>
          </>
        )}
      </Stack>
    );
  }
);

PlacesInput.propTypes = {
  apiKey: PropTypes.string,
  //libraries: PropTypes.arrayOf(PropTypes.string),
  // ref: PropTypes.oneOfType([
  //   // Either a function
  //   PropTypes.func,
  //   // Or anything shaped { current: any }
  //   PropTypes.shape({ current: PropTypes.any }),
  // ]),
  googleMapsScriptBaseUrl: PropTypes.string,
  onPlaceSelected: PropTypes.func,
  inputAutocompleteValue: PropTypes.string,
  options: PropTypes.shape({
    componentRestrictions: PropTypes.object,
    bounds: PropTypes.object,
    location: PropTypes.object,
    offset: PropTypes.number,
    origin: PropTypes.object,
    radius: PropTypes.number,
    sessionToken: PropTypes.object,
    types: PropTypes.arrayOf(PropTypes.string),
  }),
  language: PropTypes.string,
};

// export default forwardRef((props: any, ref: any) => (
//   <PlacesInput {...props} refProp={ref} />
// ));
